import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Check, ChevronsUpDown } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { useDebounce } from "use-debounce";
import type { z } from "zod";

import {
  FormErrorSummary,
  notifyInvalidSubmit,
} from "~/components/form/form-error-summary";
import {
  PristineInvalidAlert,
  usePristineInvalidAlert,
} from "~/components/pristine-invalid-alert";
import { ProfileEditHeader } from "~/components/profile-edit-header";
import { ButtonRouter } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { FormField } from "~/components/ui/form-field";
import { Input } from "~/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import {
  UnsavedChangesAlert,
  useUnsavedChangesAlert,
} from "~/components/unsaved-changes-alert";
import { useCoachEmbeddingUpdater } from "~/hooks/use-coach-embedding-updater";
import { findClosestMajorCity } from "~/lib/actions/get-closest-major-city";
import { cn } from "~/lib/cn";
import {
  PersonalInfoSchema,
  getCityOptions,
  getPersonalInfo,
  savePersonalInfo,
} from "~/lib/functions/coach-personal-info";

type FormData = z.infer<typeof PersonalInfoSchema>;

export const Route = createFileRoute(
  "/_user/my-coaching/profile/personal-info",
)({
  staticData: { title: "Personal info" },
  loader: async () => {
    const coachData = await getPersonalInfo();
    return { coachData };
  },
  component: PersonalInfoPage,
});

function PersonalInfoPage() {
  const queryClient = useQueryClient();
  const { coachData } = Route.useLoaderData();
  const { data: cityOptions = [] } = useQuery({
    queryKey: ["cityOptions"],
    queryFn: () => getCityOptions(),
    staleTime: Infinity,
  });
  const { refresh: refreshCoachEmbedding } = useCoachEmbeddingUpdater();

  const [isAutoFilling, setIsAutoFilling] = useState(false);
  const [isComboboxOpen, setIsComboboxOpen] = useState(false);

  const {
    register,
    handleSubmit,
    formState,
    watch,
    setValue,
    trigger,
    reset,
    control,
  } = useForm<FormData>({
    resolver: zodResolver(PersonalInfoSchema),
    defaultValues: coachData,
  });

  const { errors, isDirty } = formState;

  const countryOptions = useMemo(() => {
    const allCountryLabels = cityOptions
      .flatMap((group) => group.options)
      .map((country) => country.label);
    return [...new Set(allCountryLabels)].sort((a, b) => a.localeCompare(b));
  }, [cityOptions]);

  const saveMutation = useMutation({
    mutationFn: (data: FormData) => savePersonalInfo({ data }),
    onSuccess: async (freshData) => {
      toast.success("Personal information saved");
      reset(freshData);
      await refreshCoachEmbedding();
      queryClient.invalidateQueries({ queryKey: ["coach"] });
    },
    onError: () => {
      toast.error("Failed to save personal information");
    },
  });

  const onSubmit = (data: FormData) => {
    saveMutation.mutate(data);
  };

  const { showExitAlert, onDiscard, onCancel } = useUnsavedChangesAlert(
    () => isDirty,
  );

  const pristineAlert = usePristineInvalidAlert(
    () => !isDirty && Object.keys(errors).length > 0,
  );

  const watchedCity = watch("profile.primaryCity");
  const watchedCountry = watch("profile.primaryCountry");
  const watchedClosestMajorCity = watch("profile.closestMajorCity");

  const [debouncedCity] = useDebounce(watchedCity, 1000);
  const [debouncedCountry] = useDebounce(watchedCountry, 1000);

  const lastProcessedRef = useRef({ city: "", country: "" });

  const fillClosestMajorCity = useCallback(
    async (city: string, country: string) => {
      if (
        !city ||
        !country ||
        (lastProcessedRef.current.city === city &&
          lastProcessedRef.current.country === country)
      ) {
        return;
      }
      setIsAutoFilling(true);
      lastProcessedRef.current = { city, country };
      try {
        const closestCity = await findClosestMajorCity({
          data: { city, country },
        });
        if (closestCity) {
          setValue("profile.closestMajorCity", closestCity);
        }
      } catch {
        // Silent fail for autofill
      } finally {
        setIsAutoFilling(false);
      }
    },
    [setValue],
  );

  useEffect(() => {
    if (debouncedCity && debouncedCountry) {
      fillClosestMajorCity(debouncedCity, debouncedCountry);
    }
  }, [debouncedCity, debouncedCountry, fillClosestMajorCity]);

  const cityOptionsFlat = useMemo(() => {
    return cityOptions.flatMap((group) =>
      group.options.flatMap((country) =>
        country.options.map((city) => ({
          value: city.value || city.label,
          label: city.label,
          country: country.label,
          region: group.label,
          searchText: `${city.label} ${country.label}`.toLowerCase(),
        })),
      ),
    );
  }, [cityOptions]);

  const selectedCityLabel = useMemo(() => {
    if (!watchedClosestMajorCity) return "";
    const city = cityOptionsFlat.find(
      (city) => city.value === watchedClosestMajorCity,
    );
    return city?.label || watchedClosestMajorCity;
  }, [cityOptionsFlat, watchedClosestMajorCity]);

  return (
    <>
      <UnsavedChangesAlert
        show={showExitAlert}
        onDiscard={onDiscard}
        onCancel={onCancel}
      />
      <PristineInvalidAlert {...pristineAlert} />
      <form
        onSubmit={handleSubmit(onSubmit, notifyInvalidSubmit)}
        className="space-y-8"
      >
        <ProfileEditHeader
          titleAs="h2"
          title="Personal information"
          description="Basic information to identify you and communicate with you"
          form={formState}
          isSaving={saveMutation.isPending}
          username={coachData.username}
        />

        <FormErrorSummary
          errors={errors}
          labels={{
            fullName: "Full name",
            phone: "Phone",
            "profile.primaryCity": "Primary city",
            "profile.primaryCountry": "Primary country",
            "profile.closestMajorCity": "Closest major city",
            "profile.backgroundInformation": "Background information",
          }}
        />

        <Card>
          <CardHeader>
            <CardTitle>Basic information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <input {...register("id")} type="hidden" />
            <input {...register("username")} type="hidden" />
            <input {...register("profileId")} type="hidden" />

            <FormField
              label="Full name"
              required
              helpText="Your professional name as you'd like it to appear"
              error={errors.fullName?.message}
            >
              <Input
                {...register("fullName")}
                error={errors.fullName?.message}
              />
            </FormField>

            <FormField
              label="Email"
              helpText="The address you sign in with. Change it in account settings, where we confirm it with a code."
              inputId="coach-email"
              isPrivate
            >
              <div className="flex items-start gap-2">
                <Input
                  id="coach-email"
                  value={coachData.email}
                  type="email"
                  readOnly
                  disabled
                  className="flex-1"
                />
                <ButtonRouter to="/settings/profile">Change</ButtonRouter>
              </div>
            </FormField>

            <FormField
              label="Phone"
              helpText="Your primary phone number"
              required
              error={errors.phone?.message}
              isPrivate
            >
              <Input {...register("phone")} error={errors.phone?.message} />
            </FormField>

            <FormField
              label="Primary city"
              required
              helpText="Your primary city of residence."
              error={errors.profile?.primaryCity?.message}
            >
              <Input
                {...register("profile.primaryCity")}
                error={errors.profile?.primaryCity?.message}
              />
            </FormField>

            <FormField
              label="Primary country"
              required
              helpText="Your primary country of residence."
              error={errors.profile?.primaryCountry?.message}
            >
              <Controller
                name="profile.primaryCountry"
                control={control}
                render={({ field }) => (
                  <Select
                    value={field.value || ""}
                    onValueChange={(value) => {
                      field.onChange(value);
                      trigger("profile.primaryCountry");
                    }}
                  >
                    <SelectTrigger
                      className="w-full"
                      aria-invalid={Boolean(errors.profile?.primaryCountry)}
                    >
                      <SelectValue placeholder="Select a country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countryOptions.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </FormField>

            <FormField
              label="Closest major city"
              helpText="The closest major city to your primary city of residence. This will be auto-filled when you enter your city and country above."
              error={errors.profile?.closestMajorCity?.message}
            >
              <Popover open={isComboboxOpen} onOpenChange={setIsComboboxOpen}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={isComboboxOpen}
                    className="input flex items-center justify-between text-left"
                    disabled={isAutoFilling}
                  >
                    <span className="truncate">
                      {isAutoFilling
                        ? "Auto-filling..."
                        : selectedCityLabel || "Select a city..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                  <Command>
                    <CommandInput placeholder="Search cities..." />
                    <CommandList>
                      <CommandEmpty>No city found.</CommandEmpty>
                      <CommandGroup>
                        {cityOptionsFlat.map((city) => (
                          <CommandItem
                            key={city.value}
                            value={city.searchText}
                            onSelect={() => {
                              setValue("profile.closestMajorCity", city.value);
                              setIsComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                watchedClosestMajorCity === city.value
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            <div className="flex flex-col">
                              <span>{city.label}</span>
                              <span className="text-xs text-muted-foreground">
                                {city.country}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <input {...register("profile.closestMajorCity")} type="hidden" />
            </FormField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Background information</CardTitle>
            <CardDescription>
              Use this space to describe your background, expertise, tone of
              voice, style of working etc. to help our AI better understand you
              and your coaching style. This will be used when you use AI to
              generate values for certain fields.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              label="Background information"
              error={errors.profile?.backgroundInformation?.message}
            >
              <Controller
                name="profile.backgroundInformation"
                control={control}
                render={({ field }) => (
                  <Textarea
                    {...field}
                    value={field.value || ""}
                    error={errors.profile?.backgroundInformation?.message}
                    maxLength={2000}
                  />
                )}
              />
            </FormField>
          </CardContent>
        </Card>
      </form>
    </>
  );
}
